import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ListIcon, LayoutGridIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, UserIcon, MapPinIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
// ── TYPES ────────────────────────────────────────────────────────────────────
type VisitType = 'visita_venta' | 'visita_cobro' | 'reactivacion' | 'seguimiento';
type VisitStatus = 'pendiente' | 'visitado' | 'reprogramado' | 'codificacion';
type TeleventasResult = 'no_contesto' | 'promesa_pago' | 'venta_realizada' | 'seguimiento';
interface PlanEntry {
  id: string;
  fecha: Date;
  diaSemana: number;
  clienteNombre: string;
  codigoCliente: string;
  zona: string;
  vendedorId: string;
  vendedorNombre: string;
  tipoGestion: VisitType;
  estado: VisitStatus;
  hora?: string;
}
interface TeleventasEntry {
  id: string;
  clienteNombre: string;
  codigoCliente: string;
  fecha: Date;
  resultado: TeleventasResult;
  agente: string;
  notas?: string;
}
// ── HELPERS ──────────────────────────────────────────────────────────────────
const getMonday = (d: Date): Date => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};
const getDayName = (d: Date): string => {
  const names = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return names[d.getDay()];
};
const getVisitTypeColor = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'bg-blue-600 text-white border-blue-700';
    case 'visita_cobro':
      return 'bg-emerald-600 text-white border-emerald-700';
    case 'reactivacion':
      return 'bg-amber-600 text-white border-amber-700';
    case 'seguimiento':
      return 'bg-violet-600 text-white border-violet-700';
    default:
      return 'bg-slate-600 text-white';
  }
};
const getVisitTypeLabel = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'Visita de Venta';
    case 'visita_cobro':
      return 'Cobranza';
    case 'reactivacion':
      return 'Reactivación';
    case 'seguimiento':
      return 'Seguimiento';
    default:
      return type;
  }
};
const getStatusBadgeVariant = (status: VisitStatus) => {
  switch (status) {
    case 'visitado':
      return 'success';
    case 'reprogramado':
      return 'danger';
    case 'codificacion':
      return 'info';
    default:
      return 'warning';
  }
};
const getStatusLabel = (status: VisitStatus) => {
  switch (status) {
    case 'pendiente':
      return 'pendiente';
    case 'visitado':
      return 'completada';
    case 'reprogramado':
      return 'cancelada';
    case 'codificacion':
      return 'codificación';
    default:
      return status;
  }
};
const nextStatus = (current: VisitStatus): VisitStatus => {
  switch (current) {
    case 'pendiente':
      return 'visitado';
    case 'visitado':
      return 'reprogramado';
    case 'reprogramado':
      return 'pendiente';
    default:
      return 'pendiente';
  }
};
const getTeleventasResultBadge = (result: TeleventasResult) => {
  switch (result) {
    case 'venta_realizada':
      return <Badge variant="success" data-id="element-3718">Venta Realizada</Badge>;
    case 'promesa_pago':
      return <Badge variant="warning" data-id="element-3719">Promesa Pago</Badge>;
    case 'no_contesto':
      return <Badge variant="danger" data-id="element-3720">No Contestó</Badge>;
    case 'seguimiento':
      return <Badge variant="info" data-id="element-3721">Seguimiento</Badge>;
  }
};
// ── COMPONENT ────────────────────────────────────────────────────────────────
export function PlanificacionPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const isFieldVendor = !isAdmin && user?.permisos?.['new-visit'] === true;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [activeTab, setActiveTab] = useState<'planificacion' | 'televentas' | 'resumen'>('planificacion');
  const [planEntries, setPlanEntries] = useState<PlanEntry[]>([]);
  const [televentasEntries, setTeleventasEntries] = useState<TeleventasEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  // Form state — fecha stored as string "YYYY-MM-DD" to avoid timezone issues
  const [planFormDate, setPlanFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [planFormCliente, setPlanFormCliente] = useState('');
  const [planFormCodigo, setPlanFormCodigo] = useState('');
  const [planFormZona, setPlanFormZona] = useState('');
  const [planFormTipo, setPlanFormTipo] = useState<VisitType>('visita_venta');
  const [planFormVendedorId, setPlanFormVendedorId] = useState('');
  const [planFormVendedorNombre, setPlanFormVendedorNombre] = useState('');
  const [callFormCliente, setCallFormCliente] = useState('');
  const [callFormCodigo, setCallFormCodigo] = useState('');
  const [callFormResultado, setCallFormResultado] = useState<TeleventasResult>('seguimiento');
  const [callFormNotas, setCallFormNotas] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const PLAN_VISIBLE_LIMIT = 4;
  // ── SUPABASE LOAD ──────────────────────────────────────────────────────────
  const mapPlanRow = (row: any): PlanEntry => ({
    id: row.id,
    fecha: new Date(row.fecha),
    diaSemana: new Date(row.fecha).getDay(),
    clienteNombre: row.cliente_nombre,
    codigoCliente: row.codigo_cliente || '',
    zona: row.zona || '',
    vendedorId: row.vendedor_id || '',
    vendedorNombre: row.vendedor_nombre || '',
    tipoGestion: row.tipo_gestion as VisitType,
    estado: row.estado as VisitStatus,
    hora: new Date(row.fecha).toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  });
  const loadPlanEntries = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').select('*').order('fecha', {
        ascending: true
      });
      if (error) {
        console.error('Error loading plan_entries:', error);
        return;
      }
      if (data) setPlanEntries(data.map(mapPlanRow));
    } catch (err) {
      console.error('Error loading plan entries:', err);
    }
  };
  const loadTeleventasEntries = async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').select('*').order('fecha', {
        ascending: false
      }).limit(50);
      if (error) {
        console.error('Error loading televentas:', error);
        return;
      }
      if (data) {
        setTeleventasEntries(data.map((row: any): TeleventasEntry => ({
          id: row.id,
          clienteNombre: row.cliente_nombre,
          codigoCliente: row.codigo_cliente || '',
          fecha: new Date(row.fecha),
          resultado: row.resultado as TeleventasResult,
          agente: row.agente,
          notas: row.notas || undefined
        })));
      }
    } catch (err) {
      console.error('Error loading televentas:', err);
    }
  };
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      await Promise.all([loadPlanEntries(), loadTeleventasEntries()]);
      setIsLoading(false);
    };
    init();
  }, []);
  const refreshPlanData = useCallback(() => {
    loadPlanEntries();
    loadTeleventasEntries();
  }, []);
  useRealtimeRefresh(['plan_entries'], refreshPlanData);
  // ── COMPUTED ───────────────────────────────────────────────────────────────
  // Field vendors only see their own plan entries
  const visiblePlanEntries = useMemo(() => {
    if (isAdmin || !isFieldVendor || !user) return planEntries;
    return planEntries.filter(e => e.vendedorId === user.id || e.vendedorNombre === user.nombre);
  }, [planEntries, isAdmin, isFieldVendor, user]);
  const startOfWeek = useMemo(() => getMonday(currentDate), [currentDate]);
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  }, [startOfWeek]);
  const weekRangeLabel = useMemo(() => {
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 4);
    return `Semana del ${startOfWeek.getDate()} al ${end.getDate()} de ${startOfWeek.toLocaleString('es-DO', {
      month: 'short'
    })}`;
  }, [startOfWeek]);
  const currentWeekEntries = useMemo(() => {
    const start = new Date(startOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 5);
    end.setHours(0, 0, 0, 0);
    return visiblePlanEntries.filter(e => {
      const d = new Date(e.fecha);
      return d >= start && d < end;
    });
  }, [visiblePlanEntries, startOfWeek]);
  const stats = useMemo(() => ({
    programadas: currentWeekEntries.length,
    cobros: currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'pendiente').length,
    reactivaciones: currentWeekEntries.filter(e => e.tipoGestion === 'reactivacion').length,
    disponibles: Math.max(0, clients.length - currentWeekEntries.length)
  }), [currentWeekEntries, clients]);
  // ── HANDLERS ───────────────────────────────────────────────────────────────
  const handlePrevWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const handleNextWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };
  const resetPlanForm = () => {
    setPlanFormDate(new Date().toISOString().split('T')[0]);
    setPlanFormCliente('');
    setPlanFormCodigo('');
    setPlanFormZona('');
    setPlanFormTipo('visita_venta');
    // Auto-assign vendedor for field vendors
    if (isFieldVendor && user) {
      setPlanFormVendedorId(user.id);
      setPlanFormVendedorNombre(user.nombre);
    } else {
      setPlanFormVendedorId('');
      setPlanFormVendedorNombre('');
    }
    setSaveError('');
  };
  const resetCallForm = () => {
    setCallFormCliente('');
    setCallFormCodigo('');
    setCallFormResultado('seguimiento');
    setCallFormNotas('');
    setSaveError('');
  };
  const handleToggleStatus = async (entry: PlanEntry) => {
    const newStatus = nextStatus(entry.estado);
    setUpdatingStatusId(entry.id);
    try {
      const {
        error
      } = await supabaseAdmin.from('plan_entries').update({
        estado: newStatus
      }).eq('id', entry.id);
      if (!error) {
        setPlanEntries(prev => prev.map(e => e.id === entry.id ? {
          ...e,
          estado: newStatus
        } : e));
        addActivityLog('Cambió estado planificación', `Cliente: ${entry.clienteNombre} | ${entry.estado} → ${newStatus}`);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };
  const handleSavePlan = async () => {
    if (!planFormCliente || !planFormDate) return;
    setSaveError('');
    setIsSaving(true);
    try {
      // Build date with noon time to avoid timezone edge cases
      const fecha = new Date(planFormDate + 'T10:00:00');
      const monday = getMonday(fecha);
      // Determine vendedor — use form selection or current user
      const vendedorId = planFormVendedorId || user?.id || null;
      const vendedorNombre = planFormVendedorNombre || user?.nombre || 'Usuario';
      const payload: Record<string, any> = {
        fecha: fecha.toISOString(),
        dia_semana: getDayName(fecha),
        cliente_nombre: planFormCliente,
        codigo_cliente: planFormCodigo || '',
        zona: planFormZona || 'General',
        vendedor_nombre: vendedorNombre,
        tipo_gestion: planFormTipo,
        estado: 'pendiente',
        semana_inicio: monday.toISOString().split('T')[0]
      };
      // Only include vendedor_id if it's a valid value (avoid FK constraint errors)
      if (vendedorId) {
        payload.vendedor_id = vendedorId;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      // Success — add to local state immediately and close modal
      if (data && data.length > 0) {
        setPlanEntries(prev => [...prev, mapPlanRow(data[0])]);
      }
      addActivityLog('Creó planificación', `Cliente: ${planFormCliente} | Fecha: ${planFormDate} | Tipo: ${planFormTipo}`);
      setIsPlanModalOpen(false);
      resetPlanForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveCall = async () => {
    if (!callFormCliente) return;
    setSaveError('');
    setIsSaving(true);
    try {
      const payload: Record<string, any> = {
        cliente_nombre: callFormCliente,
        codigo_cliente: callFormCodigo || '',
        fecha: new Date().toISOString(),
        resultado: callFormResultado,
        agente: user?.nombre || 'Agente',
        notas: callFormNotas || ''
      };
      // Only include agente_id if valid
      if (user?.id) {
        payload.agente_id = user.id;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      if (data && data.length > 0) {
        const newEntry: TeleventasEntry = {
          id: data[0].id,
          clienteNombre: data[0].cliente_nombre,
          codigoCliente: data[0].codigo_cliente || '',
          fecha: new Date(data[0].fecha),
          resultado: data[0].resultado as TeleventasResult,
          agente: data[0].agente,
          notas: data[0].notas || undefined
        };
        setTeleventasEntries(prev => [newEntry, ...prev]);
      }
      addActivityLog('Registró llamada televentas', `Cliente: ${callFormCliente} | Resultado: ${callFormResultado}`);
      setIsCallModalOpen(false);
      resetCallForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  // ── RENDER ─────────────────────────────────────────────────────────────────
  return <div className="space-y-6" data-id="element-3722">
      <PageHeader title="Planificación Comercial" subtitle="Gestión de rutas y televentas" icon={<CalendarIcon className="w-6 h-6" data-id="element-3724" />} data-id="element-3723">
        <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-3725">
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-1 shadow-sm" data-id="element-3726">
            <button onClick={handlePrevWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-3727">
              <ChevronLeftIcon className="w-4 h-4 text-slate-500" data-id="element-3728" />
            </button>
            <span className="text-sm font-medium text-slate-700 min-w-[180px] text-center" data-id="element-3729">
              {weekRangeLabel}
            </span>
            <button onClick={handleNextWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-3730">
              <ChevronRightIcon className="w-4 h-4 text-slate-500" data-id="element-3731" />
            </button>
          </div>
          <div className="bg-white border border-slate-200 p-1 rounded-xl flex items-center shadow-sm" data-id="element-3732">
            <button onClick={() => setViewMode('calendar')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Calendario" data-id="element-3733">
              <LayoutGridIcon className="w-4 h-4" data-id="element-3734" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Lista" data-id="element-3735">
              <ListIcon className="w-4 h-4" data-id="element-3736" />
            </button>
          </div>
          <Button onClick={() => {
          resetPlanForm();
          setIsPlanModalOpen(true);
        }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3738" />} data-id="element-3737">
            Nueva
          </Button>
        </div>
      </PageHeader>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-1" data-id="element-3739">
        <StatsCard title="Visitas Programadas" value={stats.programadas} icon={<MapPinIcon className="w-5 h-5" data-id="element-3741" />} variant="primary" data-id="element-3740" />
        <StatsCard title="Cobros Pendientes" value={stats.cobros} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-3743" />} variant="success" data-id="element-3742" />
        <StatsCard title="Reactivaciones" value={stats.reactivaciones} icon={<ClockIcon className="w-5 h-5" data-id="element-3745" />} variant="warning" data-id="element-3744" />
        <StatsCard title="Disp. Televentas" value={stats.disponibles} icon={<PhoneIcon className="w-5 h-5" data-id="element-3747" />} variant="default" data-id="element-3746" />
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start animate-stagger-2" data-id="element-3748">
        {/* LEFT PANEL */}
        <div className="lg:col-span-3 space-y-6" data-id="element-3749">
          {isLoading ? <Card data-id="element-3750">
              <div className="flex items-center justify-center py-16" data-id="element-3751">
                <div className="w-8 h-8 border-3 border-slate-200 border-t-[#2952C4] rounded-full animate-spin" data-id="element-3752" />
                <span className="ml-3 text-sm text-slate-500" data-id="element-3753">
                  Cargando planificación...
                </span>
              </div>
            </Card> : viewMode === 'calendar' ? <Card padding="none" className="overflow-hidden" data-id="element-3754">
              <div className="grid grid-cols-5 bg-[#0d1b46] text-white" data-id="element-3755">
                {weekDays.map((date, i) => <div key={i} className="py-3 px-2 text-center border-r border-white/10 last:border-0" data-id="element-3756">
                    <p className="text-xs font-medium opacity-70 uppercase" data-id="element-3757">
                      {date.toLocaleDateString('es-DO', {
                  weekday: 'short'
                })}
                    </p>
                    <p className="text-lg font-bold" data-id="element-3758">{date.getDate()}</p>
                  </div>)}
              </div>
              <div className="grid grid-cols-5 min-h-[400px] bg-slate-50" data-id="element-3759">
                {weekDays.map((date, i) => {
              const dayEntries = currentWeekEntries.filter(e => {
                const entryDate = new Date(e.fecha);
                return entryDate.getDate() === date.getDate() && entryDate.getMonth() === date.getMonth() && entryDate.getFullYear() === date.getFullYear();
              });
              return <div key={i} className="border-r border-slate-200 last:border-0 p-2 space-y-2" data-id="element-3760">
                      {dayEntries.map(entry => <div key={entry.id} className={`p-2 rounded-lg shadow-sm text-white text-xs ${getVisitTypeColor(entry.tipoGestion)}`} data-id="element-3761">
                          {entry.hora && <p className="opacity-80 mb-0.5 text-[10px]" data-id="element-3762">
                              {entry.hora}
                            </p>}
                          <p className="font-bold truncate" data-id="element-3763">
                            {entry.clienteNombre}
                          </p>
                          <p className="opacity-90 truncate mt-0.5" data-id="element-3764">
                            {getVisitTypeLabel(entry.tipoGestion)}
                          </p>
                        </div>)}
                      {dayEntries.length === 0 && <div className="border-2 border-dashed border-emerald-200 bg-emerald-50 rounded-lg p-2 text-center mt-4" data-id="element-3765">
                          <p className="text-[10px] font-medium text-emerald-600" data-id="element-3766">
                            Disponible
                          </p>
                        </div>}
                    </div>;
            })}
              </div>
              <div className="p-4 border-t border-slate-100 flex flex-wrap gap-4 bg-white" data-id="element-3767">
                <div className="flex items-center gap-2" data-id="element-3768">
                  <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-3769"></div>
                  <span className="text-xs text-slate-600" data-id="element-3770">
                    Visita de Venta
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3771">
                  <div className="w-3 h-3 rounded-full bg-emerald-600" data-id="element-3772"></div>
                  <span className="text-xs text-slate-600" data-id="element-3773">Cobranza</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3774">
                  <div className="w-3 h-3 rounded-full bg-amber-600" data-id="element-3775"></div>
                  <span className="text-xs text-slate-600" data-id="element-3776">Reactivación</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-3777">
                  <div className="w-3 h-3 rounded-full bg-violet-600" data-id="element-3778"></div>
                  <span className="text-xs text-slate-600" data-id="element-3779">Seguimiento</span>
                </div>
              </div>
            </Card> : <Card padding="none" data-id="element-3780">
              <div className="overflow-x-auto" data-id="element-3781">
                <table className="w-full text-sm" data-id="element-3782">
                  <thead className="bg-slate-50 border-b border-slate-200" data-id="element-3783">
                    <tr data-id="element-3784">
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-3785">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-3786">
                        RV
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-3787">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-3788">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-3789">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-3790">
                    {currentWeekEntries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => <tr key={entry.id} className="hover:bg-slate-50" data-id="element-3791">
                          <td className="px-4 py-3" data-id="element-3792">
                            <p className="font-medium text-slate-900" data-id="element-3793">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-3794">
                              {entry.codigoCliente} • {entry.zona}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-3795">
                            {entry.vendedorNombre}
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-3796">
                            {entry.fecha.toLocaleDateString('es-DO', {
                      weekday: 'short',
                      day: 'numeric'
                    })}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-3797">
                            <Badge variant="default" className="text-xs" data-id="element-3798">
                              {getVisitTypeLabel(entry.tipoGestion)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-3799">
                            <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50" title="Clic para cambiar estado" data-id="element-3800">
                              {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-3801" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} data-id="element-3802">
                                  {getStatusLabel(entry.estado)}
                                </Badge>}
                            </button>
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={currentPage} totalPages={Math.ceil(currentWeekEntries.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} totalItems={currentWeekEntries.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-3803" />
            </Card>}
        </div>

        {/* RIGHT PANEL */}
        <div className="lg:col-span-2" data-id="element-3804">
          <Card padding="none" className="h-full flex flex-col" data-id="element-3805">
            <div className="flex border-b border-slate-200" data-id="element-3806">
              <button onClick={() => setActiveTab('planificacion')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'planificacion' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3807">
                Planificación
              </button>
              <button onClick={() => setActiveTab('televentas')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'televentas' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3808">
                Televentas
              </button>
              <button onClick={() => setActiveTab('resumen')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'resumen' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-3809">
                Resumen
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto max-h-[600px]" data-id="element-3810">
              {activeTab === 'planificacion' && <div className="space-y-4" data-id="element-3811">
                  <div className="relative" data-id="element-3812">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3813" />
                    <input type="text" placeholder="Buscar cliente..." value={planSearchQuery} onChange={e => setPlanSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-3814" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2" data-id="element-3815">
                    <span data-id="element-3816">Cliente</span>
                    <span data-id="element-3817">Estado Gestión</span>
                  </div>
                  {(() => {
                const filtered = currentWeekEntries.filter(entry => planSearchQuery ? entry.clienteNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.vendedorNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.zona.toLowerCase().includes(planSearchQuery.toLowerCase()) : true);
                const visible = planSearchQuery ? filtered : filtered.slice(0, PLAN_VISIBLE_LIMIT);
                const hiddenCount = filtered.length - visible.length;
                return <>
                        {visible.map(entry => <div key={entry.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-3818">
                            <div data-id="element-3819">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-3820">
                                {entry.clienteNombre}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3821">
                                {entry.vendedorNombre} •{' '}
                                {entry.fecha.toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short'
                        })}
                              </p>
                              <span className="inline-block mt-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600 font-medium" data-id="element-3822">
                                {getVisitTypeLabel(entry.tipoGestion)}
                              </span>
                            </div>
                            <div className="flex flex-col items-end gap-1" data-id="element-3823">
                              <div className="flex items-center gap-1.5" data-id="element-3824">
                                <span className="text-[10px] font-medium text-rose-600" data-id="element-3825">
                                  En gestión por RV
                                </span>
                                <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" data-id="element-3826"></div>
                              </div>
                              <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer" title="Clic para cambiar estado" data-id="element-3827">
                                {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-3828" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} size="sm" data-id="element-3829">
                                    {getStatusLabel(entry.estado)}
                                  </Badge>}
                              </button>
                            </div>
                          </div>)}
                        {hiddenCount > 0 && !planSearchQuery && <div className="text-center py-2" data-id="element-3830">
                            <p className="text-xs text-slate-400" data-id="element-3831">
                              +{hiddenCount} más — usa el buscador para
                              encontrarlos
                            </p>
                          </div>}
                        {filtered.length === 0 && <div className="text-center py-6" data-id="element-3832">
                            <SearchIcon className="w-6 h-6 text-slate-300 mx-auto mb-2" data-id="element-3833" />
                            <p className="text-sm text-slate-500" data-id="element-3834">
                              {planSearchQuery ? `No se encontraron resultados para "${planSearchQuery}"` : 'No hay planificaciones esta semana'}
                            </p>
                          </div>}
                      </>;
              })()}
                  <div className="pt-4 mt-4 border-t border-slate-100 grid grid-cols-2 gap-3" data-id="element-3835">
                    <div className="bg-[#2952C4] text-white p-3 rounded-lg text-center" data-id="element-3836">
                      <p className="text-xs opacity-80" data-id="element-3837">Visitas Programadas</p>
                      <p className="text-xl font-bold font-heading" data-id="element-3838">
                        {stats.programadas}
                      </p>
                    </div>
                    <div className="bg-emerald-600 text-white p-3 rounded-lg text-center" data-id="element-3839">
                      <p className="text-xs opacity-80" data-id="element-3840">Cobros Pendientes</p>
                      <p className="text-xl font-bold font-heading" data-id="element-3841">
                        {stats.cobros}
                      </p>
                    </div>
                  </div>
                </div>}

              {activeTab === 'televentas' && <div className="space-y-4" data-id="element-3842">
                  <Button className="w-full" size="sm" onClick={() => {
                resetCallForm();
                setIsCallModalOpen(true);
              }} leftIcon={<PhoneIcon className="w-4 h-4" data-id="element-3844" />} data-id="element-3843">
                    Registrar Llamada
                  </Button>
                  <div className="space-y-3" data-id="element-3845">
                    {televentasEntries.length === 0 && <div className="text-center py-8" data-id="element-3846">
                        <PhoneIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-3847" />
                        <p className="text-sm text-slate-500" data-id="element-3848">
                          No hay llamadas registradas
                        </p>
                      </div>}
                    {televentasEntries.map(entry => <div key={entry.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-3849">
                        <div className="flex justify-between items-start mb-2" data-id="element-3850">
                          <div data-id="element-3851">
                            <p className="font-semibold text-slate-900 text-sm" data-id="element-3852">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-3853">
                              {entry.codigoCliente}
                            </p>
                          </div>
                          {getTeleventasResultBadge(entry.resultado)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 border-t border-slate-200 pt-2 mt-2" data-id="element-3854">
                          <UserIcon className="w-3 h-3" data-id="element-3855" />
                          <span data-id="element-3856">{entry.agente}</span>
                          <span className="mx-1" data-id="element-3857">•</span>
                          <span data-id="element-3858">{entry.fecha.toLocaleDateString()}</span>
                        </div>
                        {entry.notas && <p className="text-xs text-slate-600 mt-2 bg-white p-2 rounded border border-slate-100 italic" data-id="element-3859">
                            "{entry.notas}"
                          </p>}
                      </div>)}
                  </div>
                </div>}

              {activeTab === 'resumen' && <div className="space-y-6" data-id="element-3860">
                  <div data-id="element-3861">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-3862">
                      Cumplimiento Semanal
                    </h4>
                    <div className="space-y-3" data-id="element-3863">
                      {(() => {
                    const visitados = currentWeekEntries.filter(e => e.estado === 'visitado').length;
                    const pctVisitas = currentWeekEntries.length ? Math.round(visitados / currentWeekEntries.length * 100) : 0;
                    const cobrosHechos = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'visitado').length;
                    const cobrosTotal = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro').length;
                    const pctCobros = cobrosTotal ? Math.round(cobrosHechos / cobrosTotal * 100) : 0;
                    return <>
                            <div data-id="element-3864">
                              <div className="flex justify-between text-xs mb-1" data-id="element-3865">
                                <span data-id="element-3866">Visitas Realizadas</span>
                                <span className="font-bold" data-id="element-3867">{pctVisitas}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-3868">
                                <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                            width: `${pctVisitas}%`
                          }} data-id="element-3869"></div>
                              </div>
                            </div>
                            <div data-id="element-3870">
                              <div className="flex justify-between text-xs mb-1" data-id="element-3871">
                                <span data-id="element-3872">Cobros Efectivos</span>
                                <span className="font-bold" data-id="element-3873">{pctCobros}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-3874">
                                <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{
                            width: `${pctCobros}%`
                          }} data-id="element-3875"></div>
                              </div>
                            </div>
                          </>;
                  })()}
                    </div>
                  </div>
                  <div data-id="element-3876">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-3877">
                      Desglose por Tipo
                    </h4>
                    <div className="space-y-2" data-id="element-3878">
                      {(['visita_venta', 'visita_cobro', 'reactivacion', 'seguimiento'] as VisitType[]).map(type => {
                    const count = currentWeekEntries.filter(e => e.tipoGestion === type).length;
                    const pct = currentWeekEntries.length ? Math.round(count / currentWeekEntries.length * 100) : 0;
                    return <div key={type} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded" data-id="element-3879">
                            <span className="font-medium text-slate-700" data-id="element-3880">
                              {getVisitTypeLabel(type)}
                            </span>
                            <div className="flex items-center gap-2" data-id="element-3881">
                              <span className="text-slate-500" data-id="element-3882">{count}</span>
                              <span className="font-bold w-8 text-right" data-id="element-3883">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* MODAL: Nueva Planificación */}
      <Modal isOpen={isPlanModalOpen} onClose={() => setIsPlanModalOpen(false)} title="Nueva Planificación" data-id="element-3884">
        <ModalBody data-id="element-3885">
          <div className="space-y-4" data-id="element-3886">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3887">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-3888" />
                {saveError}
              </div>}
            <div className="grid grid-cols-2 gap-4" data-id="element-3889">
              <div data-id="element-3890">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3891">
                  Fecha
                </label>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormDate} onChange={e => setPlanFormDate(e.target.value)} data-id="element-3892" />
              </div>
              <div data-id="element-3893">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3894">
                  Tipo Gestión
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormTipo} onChange={e => setPlanFormTipo(e.target.value as VisitType)} data-id="element-3895">
                  <option value="visita_venta" data-id="element-3896">Visita de Venta</option>
                  <option value="visita_cobro" data-id="element-3897">Visita de Cobro</option>
                  <option value="reactivacion" data-id="element-3898">Reactivación</option>
                  <option value="seguimiento" data-id="element-3899">Seguimiento</option>
                </select>
              </div>
            </div>
            <div data-id="element-3900">
              <SearchableSelect label="Cliente" value={planFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setPlanFormCliente(val);
              setPlanFormCodigo(client?.codigo || client?.id || '');
              setPlanFormZona(client?.zona || 'General');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-3901" />
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3902">
              <Input label="Código" value={planFormCodigo} readOnly className="bg-slate-50" data-id="element-3903" />
              <Input label="Zona" value={planFormZona} readOnly className="bg-slate-50" data-id="element-3904" />
            </div>
            {isAdmin && <div data-id="element-3905">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3906">
                  Vendedor
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormVendedorId} onChange={e => {
              const v = users.find(u => u.id === e.target.value);
              setPlanFormVendedorId(e.target.value);
              setPlanFormVendedorNombre(v?.nombre || '');
            }} data-id="element-3907">
                  <option value="" data-id="element-3908">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo).map(u => <option key={u.id} value={u.id} data-id="element-3909">
                        {u.nombre}
                      </option>)}
                </select>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3910">
          <Button variant="secondary" onClick={() => setIsPlanModalOpen(false)} data-id="element-3911">
            Cancelar
          </Button>
          <Button onClick={handleSavePlan} isLoading={isSaving} disabled={!planFormCliente || !planFormDate} data-id="element-3912">
            Guardar Planificación
          </Button>
        </ModalFooter>
      </Modal>

      {/* MODAL: Registrar Llamada */}
      <Modal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} title="Registrar Gestión Televentas" data-id="element-3913">
        <ModalBody data-id="element-3914">
          <div className="space-y-4" data-id="element-3915">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3916">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-3917" />
                {saveError}
              </div>}
            <div data-id="element-3918">
              <SearchableSelect label="Cliente" value={callFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setCallFormCliente(val);
              setCallFormCodigo(client?.id || '');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-3919" />
            </div>
            <div data-id="element-3920">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3921">
                Resultado
              </label>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={callFormResultado} onChange={e => setCallFormResultado(e.target.value as TeleventasResult)} data-id="element-3922">
                <option value="no_contesto" data-id="element-3923">No Contestó</option>
                <option value="promesa_pago" data-id="element-3924">Promesa de Pago</option>
                <option value="venta_realizada" data-id="element-3925">Venta Realizada</option>
                <option value="seguimiento" data-id="element-3926">Seguimiento</option>
              </select>
            </div>
            <div data-id="element-3927">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3928">
                Notas
              </label>
              <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none" placeholder="Detalles de la llamada..." value={callFormNotas} onChange={e => setCallFormNotas(e.target.value)} data-id="element-3929" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3930">
          <Button variant="secondary" onClick={() => setIsCallModalOpen(false)} data-id="element-3931">
            Cancelar
          </Button>
          <Button onClick={handleSaveCall} isLoading={isSaving} disabled={!callFormCliente} data-id="element-3932">
            Registrar Llamada
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}